import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Empty, Button } from 'antd';
import {
  AimOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useDashboardNavigation } from '../../contexts/DashboardNavigationContext';
import { MarketFitComponent } from '../../components/Dashboard/ReportingComponents';
import { NewDealDialog } from '../../components/MarketFitAnalysis/NewDealDialog';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { MarketFitDashboardNavCards } from './MarketFitDashboardNavCards';
import styles from './MarketFitDashboard.module.css';

const { Title, Text } = Typography;

// ============================================================================
// MARKETFIT DASHBOARD
// ============================================================================

export const MarketFitDashboard: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { currentLocation, goBack } = useDashboardNavigation();
  const [newDealDialogVisible, setNewDealDialogVisible] = useState(false);

  const handleAnalyzeDeal = useCallback((dealId: string, _dealName?: string) => {
    navigate(`/dashboard/product/${productId}/marketfit/analysis/${dealId}`);
  }, [navigate, productId]);

  const handleCreateNewDeal = useCallback(() => {
    setNewDealDialogVisible(true);
  }, []);

  const handleDealCreated = useCallback((dealId: string) => {
    setNewDealDialogVisible(false);
    navigate(`/dashboard/product/${productId}/marketfit/analysis/${dealId}`);
  }, [navigate, productId]);

  const handleSituationSelect = useCallback(
    (situationId: string) => {
      if (productId && isLocalBridgeApiEnabled()) {
        navigate(`/dashboard/product/${productId}/marketfit/situation/${situationId}`);
      }
    },
    [productId, navigate]
  );

  const handleSolutionSelect = useCallback(
    (solutionId: string) => {
      if (productId && isLocalBridgeApiEnabled()) {
        navigate(`/dashboard/product/${productId}/marketfit/solution/${solutionId}`);
      }
    },
    [productId, navigate]
  );

  if (!productId) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description="No product selected"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={goBack}>
            Go Back
          </Button>
        </Empty>
      </div>
    );
  }

  const productName = currentLocation.productName || 'Product';

  return (
    <div className={styles.dashboard}>
      {/* Dashboard Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <AimOutlined className={styles.headerIcon} />
          <div>
            <Title level={2} className={styles.title}>
              MarketFit - {productName}
            </Title>
            <Text className={styles.subtitle}>
              Deals, situations, solutions, and market fit analysis
            </Text>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreateNewDeal}
          >
            New Deal Analysis
          </Button>
        </div>
      </div>

      {/* Main MarketFit Component */}
      <div className={styles.mainContent}>
        <MarketFitComponent
          artifactId={productId}
          onDealSelect={handleAnalyzeDeal}
          onSituationSelect={isLocalBridgeApiEnabled() ? handleSituationSelect : undefined}
          onSolutionSelect={isLocalBridgeApiEnabled() ? handleSolutionSelect : undefined}
        />
      </div>

      <MarketFitDashboardNavCards
        onAnalyzeDeal={handleAnalyzeDeal}
        onNavigateSituationNew={() =>
          navigate(`/dashboard/product/${productId}/marketfit/situation/new`)
        }
        onNavigateSolutionNew={() =>
          navigate(`/dashboard/product/${productId}/marketfit/solution/new`)
        }
      />

      {/* New Deal Dialog */}
      <NewDealDialog
        visible={newDealDialogVisible}
        productId={productId}
        onCancel={() => setNewDealDialogVisible(false)}
        onSuccess={handleDealCreated}
      />

    </div>
  );
};

export default MarketFitDashboard;
